/*
 * Copyright (c) 2023. Collabro Limited
 */
import React, { useState } from "react";
import { PopoutScreenProps } from "../Options/Options";
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  List,
  Popover,
  Typography,
} from "@mui/material";
import { Backspace, Close, Save } from "@mui/icons-material";
import FilterInput, { FilterProps } from "./FilterInput";
import { TransitionGroup } from "react-transition-group";
import { useMutation } from "@tanstack/react-query";
import { saveFilter } from "../../api";
import { LoadingButton } from "@mui/lab";
import { AxiosError } from "axios";

export interface FilterInputProps {
  existingFilters: FilterProps[];
  setExistingFilters: any;
  filter?: FilterProps;
  options: FilterOptions[];
}

interface FilterOptions {
  label: string;
  value: string;
}

const FilterScreen = (props: PopoutScreenProps) => {
  const handleClose = () => {
    props.onClose(null);
  };

  const [existingFilters, setExistingFilters] = useState<FilterProps[]>(
    props.initialData
  );
  const handleClear = () => {
    setExistingFilters([]);
  };

  const filterOptions = [
    {
      label: "Forename",
      value: "forenames",
    },
    {
      label: "Surname",
      value: "surname",
    },
    {
      label: "Vantage Id",
      value: "personnelId",
    },
    {
      label: "Job Title",
      value: "jobTitle",
    },
    {
      label: "Employer",
      value: "companyName",
    },
    {
      label: "Core Crew",
      value: "coreCrew",
    },
    {
      label: "Gender",
      value: "sex",
    },
    {
      label: "Discipline",
      value: "discipline",
    },
    {
      label: "Department",
      value: "deptCode",
    },
    {
      label: "Team",
      value: "team",
    },
  ];

  const saveFilterToPreferences = useMutation({
    mutationFn: (filters: FilterProps[]) => {
      return saveFilter(filters);
    },
    onSuccess: () => {
      props.setInitialData(existingFilters);
      props.getLatestReportData();
      props.onClose(null);
    },
    onError: (err: AxiosError) => props.handleError(err),
  });

  const handleSaveClicked = async () => {
    if (props.initialData.length === 0 && existingFilters.length === 0) {
      props.onClose(null);
    } else {
      saveFilterToPreferences.mutate(existingFilters);
    }
  };

  return (
    <Popover
      anchorEl={props.anchorEl}
      open={Boolean(props.anchorEl)}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "center",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <div
        style={{
          padding: 10,
          width: 500,
        }}
      >
        <IconButton onClick={handleClose}>
          <Close fontSize="inherit" />
        </IconButton>
        <Divider>
          <Typography variant="h6">Filters</Typography>
        </Divider>
        <FilterInput
          options={filterOptions}
          existingFilters={existingFilters}
          setExistingFilters={setExistingFilters}
        />
        <Box>
          <List>
            <TransitionGroup>
              {existingFilters.map((filter) => (
                <Collapse key={filter.field}>
                  <FilterInput
                    options={filterOptions}
                    filter={filter}
                    existingFilters={existingFilters}
                    setExistingFilters={setExistingFilters}
                  />
                </Collapse>
              ))}
            </TransitionGroup>
          </List>
        </Box>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <LoadingButton
            sx={{ mr: 1 }}
            variant="contained"
            loading={saveFilterToPreferences.isLoading}
            size="small"
            startIcon={<Save />}
            onClick={handleSaveClicked}
          >
            Save
          </LoadingButton>
          <Button
            variant="outlined"
            size="small"
            endIcon={<Backspace />}
            onClick={handleClear}
          >
            Clear All
          </Button>
        </div>
      </div>
    </Popover>
  );
};

export default FilterScreen;
